import { TournamentTeamEntity } from './tournamentTeam'
import axios from '@/utils/request'
import { AxiosPromise } from 'axios'
import { BaseResponse, ResponseStatus } from '.'
import { MatchGameState } from './match'

export interface StageEntity {
  stageId: string
  tournamentId: string
  name: string
  number: number
  setting: StageSettingEntity
  brackets: BracketEntity[]
  isBracketVisible: boolean
  state: string
}

export enum LeagueMap {
  SummonersRift = 11,
  HowlingAbyss = 12
}

export enum LeaguePickMode {
  Blind = 1,
  Competitive = 2,
  Random = 4,
  Tournament = 6
}

export interface StageSettingEntity {
  numOfGroups: number
  bestOfN: number
  stageFormat: StageFormatType | null
  roundRobinEncounterTimes: number
  isAutoApproveIdenticalResult: boolean
  isAutoCompleteBracket: boolean
  isAutoGetGameEvidence: boolean
  isAutoGenerateMatch: boolean
  isAutoCheckInAllWhenCaptainCheckIn: boolean
  isThirdPlaceMatchEnabled: boolean
  leagueMap: LeagueMap | null
  leaguePickMode: LeaguePickMode | null
  draftId: string | null
  autoApproveResultTime: number | null
  freeForAllMaxTeamPerMatch: number | null
}

export enum StageFormat {
  SingleElimination = 'SingleElimination',
  DoubleElimination = 'DoubleElimination',
  RoundRobin = 'RoundRobin',
  FreeForAll = 'FreeForAll'
}

export interface BracketEntity {
  name: string
  groupNumber: number
  state: BracketState
  matches: BracketMatchEntity[]
}

export enum BracketState {
  Initial = 'Initial',
  InProgress = 'InProgress',
  PendingComplete = 'PendingComplete',
  Completed = 'Completed'
}

export interface BracketMatchEntity {
  id: string
  round: number
  tournamentTeams: TournamentTeamEntity[]
  scores: BracketScoreEntity[]
  nextWinnerBracketMatchId: string
}

export enum BracketScoreResult {
  None = 'None',
  Win = 'Win',
  Lose = 'Lose',
  Foul = 'Foul',
  NotCheckedIn = 'NotCheckedIn',
  Forfeit = 'Forfeit',
  Draw = 'Draw'
}

export interface BracketScoreEntity {
  tournamentTeamId: string
  winCount: number
  totalScore: number
  result: BracketScoreResult
}

export enum StageState {
  Initial = 'Initial',
  InProgress = 'InProgress',
  StandingPendingGenerate = 'StandingPendingGenerate',
  StandingPendingApproval = 'StandingPendingApproval',
  Cancelled = 'Cancelled',
  Completed = 'Completed'
}

export enum StageFormatType {
  SingleElimination = 'SingleElimination',
  DoubleElimination = 'DoubleElimination',
  RoundRobin = 'RoundRobin',
  FreeForAll = 'FreeForAll'
}

export class BracketNode {
  match: BracketMatchAdjacencyMatrixEntity | null = null
  nextNode: BracketNode | null = null
  childNode: BracketNode[] = []
  round: number = 0
  constructor(match: BracketMatchAdjacencyMatrixEntity, round: number) {
    this.match = match
    this.round = round
  }
}

export class BracketMatrix {
  bracket: BracketAdjacencyMatrixEntity | null = null
  data: BracketNode[] = []

  constructor(bracket: BracketAdjacencyMatrixEntity) {
    this.bracket = bracket
    this.data = []
  }

  findOrCreateNode(match: BracketMatchAdjacencyMatrixEntity) {
    let node = null
    for (const n of this.data) {
      if (n.match === match) {
        node = n
        break
      }
    }
    if (!node) {
      node = new BracketNode(match, this.getMatchRound(match))
      this.data.push(node)
    }
    return node
  }

  toAdjacencyMatrix() {
    if (!this.bracket) {
      return null
    }
    const matches = this.bracket.matches
    const edges = this.bracket.edges

    // initialize matrix with sizeof(vertices) * sizeof(vertices) array
    const matrix: number[][] = Array.from(Array(matches.length), _ =>
      Array(matches.length).fill(0)
    )

    // fill matrix with edges x, y coordinate
    for (var i = 0; i < edges.length; i++) {
      const x = edges[i][0]
      const y = edges[i][1]
      matrix[y][x] = 1
    }

    return matrix
  }

  toBracketTree() {
    if (!this.bracket) {
      return null
    }

    this.data = []
    for (const [x, y] of this.bracket.edges) {
      // find node
      const node = this.findOrCreateNode(this.bracket.matches[y])
      // find leaf
      const leaf = this.findOrCreateNode(this.bracket.matches[x])
      leaf.nextNode = node
      node.childNode.push(leaf)
    }

    // if there are no edges
    if (this.bracket.edges.length === 0) {
      for (const match of this.bracket.matches) {
        this.data.push(this.findOrCreateNode(match))
      }
    }
    // additional matches
    if (this.bracket.additionalMatches) {
      for (const match of this.bracket.additionalMatches) {
        this.findOrCreateNode(match)
      }
    }
    return this.data
  }

  getMatchRound(match: BracketMatchAdjacencyMatrixEntity) {
    // find leaf node round
    let leafNodeRoundOf = 2
    if (!this.bracket) return leafNodeRoundOf

    for (const match of this.bracket!.matches) {
      if (match.roundOf > leafNodeRoundOf) {
        leafNodeRoundOf = match.roundOf
      }
    }

    // find match round
    let round = 1
    while (leafNodeRoundOf > match.roundOf) {
      round++
      leafNodeRoundOf /= 2
    }

    return round
  }

  toBracketTreeNoEdge() {
    const allRoundOfs = this.bracket!.matches.map(bm => bm.roundOf)
    const roundOfs = allRoundOfs
      .filter((roundOf, index) => allRoundOfs.indexOf(roundOf) === index)
      .sort((a, b) => a - b)

    return roundOfs.map(roundOf =>
      [...this.bracket!.matches, ...this.bracket!.additionalMatches]
        .filter(bm => bm.roundOf === roundOf)
        .sort((a, b) => a.identifier.localeCompare(b.identifier))
    )
  }

  toBracketTreeLevelOrdered() {
    const tree = this.toBracketTree()
    if (!tree) {
      return null
    }

    // find the root node where nextNode is empty
    const bracketNodeRoot = tree.find(rootNode => rootNode.nextNode === null)

    if (!bracketNodeRoot) {
      // returns null when failed to find root
      return null
    }

    const initialRound = 0
    const roundsMap: Map<
      number,
      BracketMatchAdjacencyMatrixEntity[]
    > = new Map()
    const queue = []

    let totalRounds = 0

    // push the root node to our queue (base round level)
    queue.push([bracketNodeRoot, initialRound])

    while (queue.length > 0) {
      const qFront = queue.shift()!

      const node = qFront[0] as BracketNode
      const currentRound = qFront[1] as number

      totalRounds = Math.max(totalRounds, currentRound)

      let roundMatches: BracketMatchAdjacencyMatrixEntity[] = []

      // if item is same round as previously processed
      // get the previous matches from map to roundMatches.
      if (roundsMap.has(currentRound)) {
        roundMatches = roundsMap.get(currentRound)!
      }

      if (node.match) roundMatches.push(node.match)

      // set roundsMap for this round
      roundsMap.set(currentRound, roundMatches)

      const leftNode: BracketNode = node.childNode[0]!
      const rightNode: BracketNode = node.childNode[1]!
      const childRound = currentRound + 1

      if (leftNode) {
        queue.push([leftNode, childRound])
      }
      if (rightNode) {
        queue.push([rightNode, childRound])
      }
    }
    const rounds: BracketMatchAdjacencyMatrixEntity[][] = []

    // loop inversely
    for (let i = totalRounds; i >= 0; i--) {
      const rdMatches = roundsMap.get(i)!
      rounds.push(rdMatches)
    }

    // add additional nodes
    const additionalNodes = tree.filter(
      node => node.match && node.match!.isAdditional
    )
    additionalNodes.map(node => {
      const roundIndex = node.round - 1
      if (!rounds[roundIndex]) rounds[roundIndex] = []
      rounds[roundIndex].push(node.match!)
    })

    return rounds
  }
}

export interface RoundSettingEntity {
  roundOf: number
  name: string | null
}

export interface BracketAdjacencyMatrixEntity {
  bracketId: string
  groupNumber: number
  name: string
  state: string
  matches: BracketMatchAdjacencyMatrixEntity[]
  additionalMatches: BracketMatchAdjacencyMatrixEntity[]
  edges: number[][]
  roundSettings: RoundSettingEntity[]
}

export interface BracketMatchAdjacencyMatrixEntity {
  bracketMatchId: string
  matchId: string
  teams: TournamentTeamListItemEntity[]
  scores: BracketScoreEntity[]
  checkInStartTime: number
  checkInEndTime: number
  isAdvanced: boolean
  isAdditional: boolean
  roundOf: number
  identifier: string
  matchGame?: BracketMatchGameEntity
}

export interface BracketMatchGameEntity {
  currentRound: number
  state: MatchGameState
  totalRound: number
  checkIn: number
  checkInTotal: number
}

export interface StageAdjacencyMatrixEntity {
  stageId: string | null
  tournamentId: string | null
  name: string | null
  number: number
  setting: StageSettingEntity
  brackets: BracketAdjacencyMatrixEntity[] | null
}

export interface TournamentTeamListItemEntity {
  tournamentTeamId: string
  name: string
  logo: string
  state: string
}

export interface TournamentStageRequest {
  name: string
  numOfGroups: number
  stageFormatType: StageFormatType
  bestOfN: number
}

export interface StageListItemEntity {
  name: string | null
  number: number
  stageId: string
  state: StageState
}

export class StageAPI {
  public static API_PREFIX = '/tournament/v1'

  /**
   * Get stages by tournament ID
   * @param tournamentId Tournament Id
   * @returns {BaseResponse<StageListItemEntity[]>} Array of StageListItemEntity Response
   */
  public static getTournamentStages(
    tournamentId: string
  ): AxiosPromise<BaseResponse<StageListItemEntity[]>> {
    return axios.get(`${this.API_PREFIX}/tournaments/${tournamentId}/stages`)
  }

  /**
   * Get stage
   * @param tournamentId Tournament ID
   * @returns {StageEntity} StageEntity
   */
  public static getStage(
    stageId: string
  ): AxiosPromise<BaseResponse<StageEntity>> {
    return axios.get(`${this.API_PREFIX}/stages/${stageId}`)
  }
}
